import React, { useEffect, useState } from "react";
import api from "../utils/api";
import HeatmapChart from "../components/HeatmapChart";
import Graph from "../components/Graph";
import "./EnvironmentTab.css";

const HOURS_LABELS = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, "0")}:00`);

const getStatus = (temp, humidity) => {
  if (temp === null || humidity === null) return "offline";

  // Check temperature status (normal 18-30, warning <18 or 30-35, high 35-40, critical >40)
  let tempStatus = "normal";
  if (temp < 18 || (temp > 30 && temp <= 35)) {
    tempStatus = "warning";
  } else if (temp > 35 && temp <= 40) {
    tempStatus = "high";
  } else if (temp > 40) {
    tempStatus = "critical";
  }

  // Check humidity status (normal 40-60, warning <40 or 60-70, high 70-80, critical >80)
  let humStatus = "normal";
  if (humidity < 40 || (humidity > 60 && humidity <= 70)) {
    humStatus = "warning";
  } else if (humidity > 70 && humidity <= 80) {
    humStatus = "high";
  } else if (humidity > 80) {
    humStatus = "critical";
  }

  // Combine severity: critical > high > warning > normal
  const statusHierarchy = { normal: 1, warning: 2, high: 3, critical: 4 };
  const tempSeverity = statusHierarchy[tempStatus];
  const humSeverity = statusHierarchy[humStatus];

  if (tempSeverity >= humSeverity) return tempStatus;
  return humStatus;
};

const getTempStatus = (temp) => {
  if (temp === null) return "offline";
  if (temp < 18 || (temp > 30 && temp <= 35)) return "warning";
  if (temp > 35 && temp <= 40) return "high";
  if (temp > 40) return "critical";
  return "normal";
};

const getHumStatus = (humidity) => {
  if (humidity === null) return "offline";
  if (humidity < 40 || (humidity > 60 && humidity <= 70)) return "warning";
  if (humidity > 70 && humidity <= 80) return "high";
  if (humidity > 80) return "critical";
  return "normal";
};

const formatTimeShort = (isoString) => {
  if (!isoString) return "—";
  try {
    const d = new Date(isoString);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch (e) {
    return isoString;
  }
};

const formatDateToInput = (date) => {
  if (!date || isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  return `${year}-${month}-${day}`;
};

const EnvironmentTab = () => {
  const [sensors, setSensors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Selected Room Sensor
  const [selectedMeterId, setSelectedMeterId] = useState(null);

  // Heatmap controls and state
  const [parameter, setParameter] = useState("temperature");
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    // Default ending date to yesterday
    const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    // Default starting date to 7 days before yesterday
    const start = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate() - 7);
    return { start, end: yesterday };
  });

  const [heatmapRows, setHeatmapRows] = useState([]);
  const [heatmapMin, setHeatmapMin] = useState(null);
  const [heatmapMax, setHeatmapMax] = useState(null);
  const [heatmapLoading, setHeatmapLoading] = useState(false);

  // Line Graph controls and state
  const [graphParameter, setGraphParameter] = useState("temperature");
  const [graphDateRange, setGraphDateRange] = useState(() => {
    const today = new Date();
    const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    const start = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate() - 7);
    return { start, end: yesterday };
  });
  
  const [graphData, setGraphData] = useState([]);
  const [graphLoading, setGraphLoading] = useState(false);

  const username = localStorage.getItem("username") || "";

  // Poll active sensors list (standard live metric readings)
  useEffect(() => {
    if (!username) {
      setError("No user session found. Please log in again.");
      setLoading(false);
      return;
    }

    const fetchSensors = async () => {
      try {
        const res = await api.get(`/environment-sensors?username=${username}`);
        if (res.data && res.data.status === "success") {
          const sensorData = res.data.data || [];
          setSensors(sensorData);
          
          // Auto-select first sensor if missing
          if (sensorData.length > 0) {
            setSelectedMeterId((currentId) => {
              const exists = sensorData.some((s) => s.meter_id === currentId);
              return currentId && exists ? currentId : sensorData[0].meter_id;
            });
          }
          setError(null);
        } else {
          setError("Failed to retrieve environmental sensors.");
        }
      } catch (err) {
        console.error("Fetch sensors error:", err);
        setError("Error connecting to server. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchSensors();
    const interval = setInterval(fetchSensors, 10000); // refresh list every 10 seconds
    return () => clearInterval(interval);
  }, [username]);

  // Load heatmap data on controls change (NO polling/blinking, fetch on demand)
  const startTime = dateRange.start.getTime();
  const endTime = dateRange.end.getTime();

  useEffect(() => {
    if (!username) return;

    const fetchHeatmap = async () => {
      setHeatmapLoading(true);
      try {
        const startStr = `${formatDateToInput(dateRange.start)} 00:00:00`;
        const endStr = `${formatDateToInput(dateRange.end)} 23:59:59`;
        const res = await api.get(
          `/environment-sensors/heatmap?username=${username}&parameter=${parameter}&start_date=${encodeURIComponent(startStr)}&end_date=${encodeURIComponent(endStr)}`
        );
        if (res.data && res.data.status === "success") {
          setHeatmapRows(res.data.data || []);
          setHeatmapMin(res.data.minValue);
          setHeatmapMax(res.data.maxValue);
        }
      } catch (err) {
        console.error("Fetch heatmap error:", err);
      } finally {
        setHeatmapLoading(false);
      }
    };

    fetchHeatmap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameter, startTime, endTime, username]);

  // Load Line Graph historical trend points
  const graphStartTime = graphDateRange.start.getTime();
  const graphEndTime = graphDateRange.end.getTime();

  useEffect(() => {
    if (!username || !selectedMeterId) return;

    const fetchGraphData = async () => {
      setGraphLoading(true);
      try {
        const startStr = `${formatDateToInput(graphDateRange.start)} 00:00:00`;
        const endStr = `${formatDateToInput(graphDateRange.end)} 23:59:59`;
        const res = await api.get(
          `/environment-sensors/${selectedMeterId}/history?username=${username}&start_date=${encodeURIComponent(startStr)}&end_date=${encodeURIComponent(endStr)}`
        );
        if (res.data && res.data.status === "success") {
          setGraphData(res.data.data || []);
        }
      } catch (err) {
        console.error("Fetch graph history error:", err);
      } finally {
        setGraphLoading(false);
      }
    };

    fetchGraphData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMeterId, graphParameter, graphStartTime, graphEndTime, username]);

  if (loading && sensors.length === 0) {
    return (
      <div className="env-container env-loading">
        <div className="env-spinner"></div>
        <p>Loading environment monitoring system...</p>
      </div>
    );
  }

  if (error && sensors.length === 0) {
    return (
      <div className="env-container">
        <div className="env-error-card">
          <h3>⚠️ Service Interrupted</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Handle empty state
  if (sensors.length === 0) {
    return (
      <div className="env-container">
        <div className="env-empty-card">
          <div className="env-empty-icon">🌡️</div>
          <h2>No Environment Sensors Found</h2>
          <p>
            Configure and register room temperature and humidity sensors in the
            settings page to start receiving live readings.
          </p>
          <div className="env-empty-hint">
            <strong>Hint:</strong> When registering physical meters, select brand{" "}
            <code>Irhajhus</code> and model <code>TCM-T07</code> to automatically classify them as environmental.
          </div>
        </div>
      </div>
    );
  }

  // Calculate high level summaries
  const validSensors = sensors.filter(
    (s) => s.temperature !== null && s.humidity !== null
  );
  const avgTemp =
    validSensors.length > 0
      ? validSensors.reduce((sum, s) => sum + s.temperature, 0) /
        validSensors.length
      : null;
  const avgHum =
    validSensors.length > 0
      ? validSensors.reduce((sum, s) => sum + s.humidity, 0) /
        validSensors.length
      : null;

  const activeSensorsCount = sensors.length;
  const outOfRangeCount = sensors.filter((s) => {
    const status = getStatus(s.temperature, s.humidity);
    return status !== "normal" && status !== "offline";
  }).length;

  const valueFormatter = (value) => {
    if (value === null || Number.isNaN(value)) return "—";
    return parameter === "temperature"
      ? `${value.toFixed(1)} °C`
      : `${Math.round(value)} %`;
  };

  const yesterdayDate = new Date();
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterdayStr = formatDateToInput(yesterdayDate);

  const tempStatus = getTempStatus(avgTemp);
  const humStatus = getHumStatus(avgHum);


  // Format line graph data
  const chartLabels = graphData.map((d) => {
    try {
      const date = new Date(d.timestamp);
      return date.toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return d.timestamp;
    }
  });

  const chartDataPoints = graphData.map((d) =>
    graphParameter === "temperature" ? d.temperature : d.humidity
  );

  return (
    <div className="env-container">
      {/* Top Level Metrics Row */}
      <div className="env-summary-row">
        {/* Card 1: Avg Temp */}
        <div className={`env-summary-card status-${tempStatus}`}>
          <div className="env-summary-head">
            <span className="env-summary-icon">🌡️</span>
            <span className="env-summary-label">Average Temperature</span>
          </div>
          <div className="env-summary-value">
            {avgTemp !== null ? `${avgTemp.toFixed(1)} °C` : "— °C"}
          </div>
          <div className="env-summary-sub">Target range: 18 to 30 °C</div>
        </div>

        {/* Card 2: Avg Humidity */}
        <div className={`env-summary-card status-${humStatus}`}>
          <div className="env-summary-head">
            <span className="env-summary-icon">💧</span>
            <span className="env-summary-label">Average Humidity</span>
          </div>
          <div className="env-summary-value">
            {avgHum !== null ? `${Math.round(avgHum)} %` : "— %"}
          </div>
          <div className="env-summary-sub">Target range: 40–60 %</div>
        </div>

        {/* Card 3: Active Rooms */}
        <div className="env-summary-card status-normal">
          <div className="env-summary-head">
            <span className="env-summary-icon">🏢</span>
            <span className="env-summary-label">Active Sensors</span>
          </div>
          <div className="env-summary-value">{activeSensorsCount}</div>
          <div className="env-summary-sub">Connected client rooms</div>
        </div>

        {/* Card 4: Rooms Out of Range */}
        <div
          className={`env-summary-card ${
            outOfRangeCount > 0 ? "status-critical" : "status-normal"
          }`}
        >
          <div className="env-summary-head">
            <span className="env-summary-icon">⚠️</span>
            <span className="env-summary-label">Rooms Out of Range</span>
          </div>
          <div className="env-summary-value">{outOfRangeCount}</div>
          <div className="env-summary-sub">
            {outOfRangeCount === 0 ? "All parameters normal" : "Requires attention"}
          </div>
        </div>
      </div>

      {/* Main Split Grid */}
      <div className="env-main-grid">
        {/* Left Side: Sensor Grid List Container */}
        <div className="env-sensor-section">
          <div>
            <div className="env-section-title">
              <span className="env-live-heading-badge">Live Updates</span>
            </div>

            <div className="env-sensor-list">
              {sensors.map((sensor) => {
                const status = getStatus(sensor.temperature, sensor.humidity);
                const isSelected = sensor.meter_id === selectedMeterId;
                return (
                  <div
                    key={sensor.meter_id}
                    className={`env-sensor-card status-${status} ${isSelected ? "selected" : ""}`}
                    onClick={() => setSelectedMeterId(sensor.meter_id)}
                  >
                    <div className="env-card-info">
                      <h4 className="env-card-title">{sensor.location_name}</h4>
                      <span className="env-card-meter-id">ID: {sensor.meter_id}</span>
                    </div>

                    <div className="env-card-metrics">
                      <div className="env-card-metric text-temp">
                        <span className="metric-icon">🌡️</span>
                        <strong className="metric-val">
                          {sensor.temperature !== null
                            ? `${sensor.temperature.toFixed(1)}°C`
                            : "—"}
                        </strong>
                      </div>
                      <div className="env-card-metric text-humidity">
                        <span className="metric-icon">💧</span>
                        <strong className="metric-val">
                          {sensor.humidity !== null
                            ? `${Math.round(sensor.humidity)}%`
                            : "—"}
                        </strong>
                      </div>
                    </div>

                    <div className="env-card-footer">
                      <span className={`status-pill pill-${status}`}>
                        {status.toUpperCase()}
                      </span>
                      <span className="env-card-time">
                        {sensor.last_reading ? formatTimeShort(sensor.last_reading) : "Never"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Side: Heatmap Dashboard */}
        <div className="env-heatmap-section">
          <div className="env-heatmap-controls">
            {/* Toggles */}
            <div className="env-toggle-group">
              <button
                className={`env-toggle-btn ${parameter === "temperature" ? "active active-temp" : ""}`}
                onClick={() => setParameter("temperature")}
              >
                🌡️ Temp (°C)
              </button>
              <button
                className={`env-toggle-btn ${parameter === "humidity" ? "active active-humidity" : ""}`}
                onClick={() => setParameter("humidity")}
              >
                💧 Humidity (%)
              </button>
            </div>

            {/* Date range selectors */}
            <div className="env-datetime-group">
              <div className="env-date-field">
                <label className="env-select-label">From:</label>
                <input
                  type="date"
                  value={formatDateToInput(dateRange.start)}
                  onChange={(e) => {
                    const val = new Date(e.target.value + "T00:00:00");
                    if (!isNaN(val.getTime())) {
                      setDateRange((prev) => ({ ...prev, start: val }));
                    }
                  }}
                  max={formatDateToInput(dateRange.end)}
                  className="env-date-input"
                />
              </div>
              <span className="env-date-separator">TO</span>
              <div className="env-date-field">
                <label className="env-select-label">To:</label>
                <input
                  type="date"
                  value={formatDateToInput(dateRange.end)}
                  onChange={(e) => {
                    const val = new Date(e.target.value + "T23:59:59");
                    if (!isNaN(val.getTime())) {
                      setDateRange((prev) => ({ ...prev, end: val }));
                    }
                  }}
                  min={formatDateToInput(dateRange.start)}
                  max={yesterdayStr}
                  className="env-date-input"
                />
              </div>
            </div>
          </div>

          <div className="env-heatmap-chart-wrapper">
            <HeatmapChart
              rows={heatmapRows}
              timeLabels={HOURS_LABELS}
              minValue={heatmapMin}
              maxValue={heatmapMax}
              loading={heatmapLoading}
              metricLabel={parameter === "temperature" ? "Average Temperature" : "Average Humidity"}
              valueFormatter={valueFormatter}
              machineColor="multi"
              rowHeaderLabel="Sensor"
              metricType={parameter}
            />
          </div>
        </div>
      </div>

      {/* Row 3: Historical Line Graph */}
      <div className="env-history-graph-section">
        <div className="env-graph-header">
          <h3>Historical Trend</h3>
          
          <div className="env-heatmap-controls" style={{ borderBottom: "none", paddingBottom: 0 }}>
            {/* Sensor dropdown selector */}
            <div className="env-date-field" style={{ marginRight: "12px" }}>
              <label className="env-select-label">Sensor:</label>
              <select
                value={selectedMeterId || ""}
                onChange={(e) => setSelectedMeterId(e.target.value)}
                className="env-date-input"
                style={{ paddingRight: "28px", cursor: "pointer" }}
              >
                {sensors.map((s) => (
                  <option key={s.meter_id} value={s.meter_id}>
                    {s.location_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Toggles */}
            <div className="env-toggle-group">
              <button
                className={`env-toggle-btn ${graphParameter === "temperature" ? "active active-temp" : ""}`}
                onClick={() => setGraphParameter("temperature")}
              >
                🌡️ Temp (°C)
              </button>
              <button
                className={`env-toggle-btn ${graphParameter === "humidity" ? "active active-humidity" : ""}`}
                onClick={() => setGraphParameter("humidity")}
              >
                💧 Humidity (%)
              </button>
            </div>

            {/* Date range selectors */}
            <div className="env-datetime-group">
              <div className="env-date-field">
                <label className="env-select-label">From:</label>
                <input
                  type="date"
                  value={formatDateToInput(graphDateRange.start)}
                  onChange={(e) => {
                    const val = new Date(e.target.value + "T00:00:00");
                    if (!isNaN(val.getTime())) {
                      setGraphDateRange((prev) => ({ ...prev, start: val }));
                    }
                  }}
                  max={formatDateToInput(graphDateRange.end)}
                  className="env-date-input"
                />
              </div>
              <span className="env-date-separator">TO</span>
              <div className="env-date-field">
                <label className="env-select-label">To:</label>
                <input
                  type="date"
                  value={formatDateToInput(graphDateRange.end)}
                  onChange={(e) => {
                    const val = new Date(e.target.value + "T23:59:59");
                    if (!isNaN(val.getTime())) {
                      setGraphDateRange((prev) => ({ ...prev, end: val }));
                    }
                  }}
                  min={formatDateToInput(graphDateRange.start)}
                  max={yesterdayStr}
                  className="env-date-input"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="env-line-chart-wrapper">
          {graphLoading ? (
            <div className="env-chart-loading-overlay">
              <div className="env-spinner"></div>
              <p>Fetching room historical telemetry...</p>
            </div>
          ) : graphData.length === 0 ? (
            <div className="env-chart-empty">
              <span>📉</span>
              <p>No historical telemetry logged for this period.</p>
            </div>
          ) : (
            <Graph
              labels={chartLabels}
              data={chartDataPoints}
              title={graphParameter === "temperature" ? "Temperature" : "Humidity"}
              valueFormatter={(v) =>
                graphParameter === "temperature"
                  ? `${v.toFixed(1)} °C`
                  : `${Math.round(v)} %`
              }
              datasetOptions={{
                borderColor: graphParameter === "temperature" ? "#5e9f87" : "#00b8cf",
                backgroundColor:
                  graphParameter === "temperature"
                    ? "rgba(94, 159, 135, 0.1)"
                    : "rgba(0, 184, 207, 0.1)",
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default EnvironmentTab;
